<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useJabvoxPermissions } from 'dashboard/composables/useJabvoxPermissions';
import { useWindowSize, useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import TicketsSidebarLeaf from './TicketsSidebarLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const JABVOX_LOGO_URL = '/jabvox/public/logo.png';

const { accountScopedRoute, isOnJabvoxCloud } = useAccount();
const { isEnterprise } = useConfig();
const { checkPermissions } = usePolicy();
const { jabvoxCan } = useJabvoxPermissions();
const store = useStore();

// Calls run on the enterprise-only API (cloud runs enterprise); hide the entry
// on community so it doesn't lead to a dashboard/CTA the backend can't serve.
const isCallsAvailable = computed(() => isOnJabvoxCloud.value || isEnterprise);
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const saldoStatus = useMapGetter('jabvoxSaldo/getStatus');

// Reports visibility mirrors the API gate: admins always, otherwise the
// jabvox_reports_view permission from the user's custom role.
const canViewJabvoxReports = computed(() => jabvoxCan('jabvox_reports_view'));

const isSaldoEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.JABVOX_SALDO)
);

const appStatesForPresence = useMapGetter('jabvoxAppStates/getActiveStates');
const currentAppState = useMapGetter('jabvoxAppStates/getCurrentUserAppState');
const presenceDropdownOpen = ref(false);
const presenceColor = computed(() => currentAppState.value?.color || '#22c55e');
const presenceName = computed(
  () =>
    currentAppState.value?.name ||
    t('JABVOX_MANAGEMENT_STATES.APP_STATES.ACTIVE_DEFAULT')
);
const setPresence = async stateId => {
  await store.dispatch('jabvoxAppStates/setPresence', stateId);
  presenceDropdownOpen.value = false;
};

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const hasFilteredUnreadCounts = computed(() => {
  return (
    hasConversationUnreadCounts.value &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
    )
  );
});

const hasDataImport = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.DATA_IMPORT
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const mentionsUnreadCount = useMapGetter(
  'conversationUnreadCounts/getMentionsUnreadCount'
);
const participatingUnreadCount = useMapGetter(
  'conversationUnreadCounts/getParticipatingUnreadCount'
);
const unattendedUnreadCount = useMapGetter(
  'conversationUnreadCounts/getUnattendedUnreadCount'
);
const getFolderUnreadCount = useMapGetter(
  'conversationUnreadCounts/getFolderUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  // Las vistas de contacto ya no se pintan en el menú, pero la lista de contactos y el
  // diálogo de exportación las leen del store sin cargarlas ellos mismos: si se quita este
  // dispatch, sus selectores de segmento se quedan vacíos.
  store.dispatch('customViews/get', 'contact');
  if (isSaldoEnabled.value) {
    store.dispatch('jabvoxSaldo/fetchStatus');
  }
  // Load the internal-chat unread total so the sidebar badge shows without opening the chat.
  if (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_INTERNAL_CHAT
    )
  ) {
    store.dispatch('jabvoxInternalChat/fetchUnreadCount');
  }
  store.dispatch('jabvoxFieldVisibilities/fetchMyVisibilities');
  store.dispatch('jabvoxAppStates/fetchStates');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

const hasUnreadCountsForSection = section => {
  if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
    return hasFilteredUnreadCounts.value;
  }

  return hasConversationUnreadCounts.value;
};

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasUnreadCountsForSection(section),
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
    unreadCountKey: view => getFolderUnreadCount.value(view.id),
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const sortedInboxes = computed(() =>
  sortSidebarItems(inboxes.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
    labelKey: inbox => inbox.name,
    unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
  })
);

const sortedLabels = computed(() =>
  sortSidebarItems(labels.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
    labelKey: label => label.title,
    unreadCountKey: label => getLabelUnreadCount.value(label.id),
  })
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const saldoLabel = computed(() => {
  if (!saldoStatus.value) {
    return '...';
  }

  const balance = Number(saldoStatus.value.balance || 0);
  const formatted = balance.toLocaleString('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `$${formatted} COP`;
});

const saldoBalanceColor = computed(() => {
  if (!saldoStatus.value) return 'text-n-slate-11';
  const balance = Number(saldoStatus.value.balance || 0);
  if (balance > 0) return 'text-green-500';
  if (balance < 0) return 'text-red-500';
  return 'text-amber-500';
});

const menuItems = computed(() => {
  // These views are toggled per-account from Super Admin via feature flags.
  const hasFeature = flag =>
    isFeatureEnabledonAccount.value(accountId.value, flag);

  return [
    ...(hasFeature(FEATURE_FLAGS.INBOX_VIEW)
      ? [
          {
            name: 'Inbox',
            label: t('SIDEBAR.INBOX'),
            icon: 'i-lucide-inbox',
            to: accountScopedRoute('inbox_view'),
            activeOn: ['inbox_view', 'inbox_view_conversation'],
            getterKeys: {
              count: 'notifications/getUnreadCount',
            },
          },
        ]
      : []),
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          badgeCount: allUnreadCount.value,
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        ...(hasFeature(FEATURE_FLAGS.MENTIONS)
          ? [
              {
                name: 'Mentions',
                label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
                icon: 'i-lucide-at-sign',
                badgeCount: hasFilteredUnreadCounts.value
                  ? mentionsUnreadCount.value
                  : 0,
                activeOn: ['conversation_through_mentions'],
                to: accountScopedRoute('conversation_mentions'),
              },
            ]
          : []),
        ...(hasFeature(FEATURE_FLAGS.PARTICIPATING)
          ? [
              {
                name: 'Participating',
                label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
                icon: 'i-lucide-user-round-check',
                badgeCount: hasFilteredUnreadCounts.value
                  ? participatingUnreadCount.value
                  : 0,
                activeOn: ['conversation_through_participating'],
                to: accountScopedRoute('conversation_participating'),
              },
            ]
          : []),
        ...(hasFeature(FEATURE_FLAGS.UNATTENDED)
          ? [
              {
                name: 'Unattended',
                activeOn: ['conversation_through_unattended'],
                label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
                icon: 'i-lucide-clock-alert',
                badgeCount: hasFilteredUnreadCounts.value
                  ? unattendedUnreadCount.value
                  : 0,
                to: accountScopedRoute('conversation_unattended'),
              },
            ]
          : []),
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            badgeCount: hasFilteredUnreadCounts.value
              ? getFolderUnreadCount.value(view.id)
              : 0,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            badgeCount: getInboxUnreadCount.value(inbox.id),
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                badgeCount: leafProps.badgeCount,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            badgeCount: getLabelUnreadCount.value(label.id),
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // "Contactos" siempre visible y como entrada ÚNICA: antes se ocultaba cuando el módulo
    // Leads estaba activo, porque aquella pantalla absorbía la gestión de contactos.
    // Retirado Leads, esta es la única pantalla, así que va directa sin sub-secciones.
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: [
        'contacts_dashboard_index',
        'contacts_edit',
        'contacts_dashboard_active',
        'contacts_dashboard_segments_index',
        'contacts_edit_segment',
        'contacts_dashboard_labels_index',
        'contacts_edit_label',
      ],
    },
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_KANBAN
    )
      ? [
          {
            name: 'Jabvox Kanban',
            label: t('SIDEBAR.JABVOX_KANBAN'),
            icon: 'i-lucide-kanban',
            to: accountScopedRoute('jabvox_kanban_index'),
            activeOn: ['jabvox_kanban_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_AI_CHAT
    )
      ? [
          {
            name: 'Jabvox AI Chat',
            label: t('JABVOX_AI_CHAT.SIDEBAR_LABEL'),
            icon: 'i-ri-robot-2-line',
            to: accountScopedRoute('jabvox_ai_chat_index'),
            activeOn: ['jabvox_ai_chat_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_AI_CHAT_PROVISIONING
    )
      ? [
          {
            name: 'Jabvox Provisioning Chat',
            label: t('JABVOX_PROVISIONING_CHAT.SIDEBAR_LABEL'),
            icon: 'i-lucide-server-cog',
            to: accountScopedRoute('jabvox_provisioning_chat_index'),
            activeOn: ['jabvox_provisioning_chat_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_CALENDAR
    )
      ? [
          {
            name: 'Jabvox Calendar',
            label: t('JABVOX_CALENDAR.SIDEBAR_LABEL'),
            icon: 'i-lucide-calendar-days',
            to: accountScopedRoute('jabvox_calendar_index'),
            activeOn: ['jabvox_calendar_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_TASKS
    )
      ? [
          {
            name: 'Jabvox Tasks',
            label: t('SIDEBAR.JABVOX_TASKS'),
            icon: 'i-lucide-square-kanban',
            to: accountScopedRoute('jabvox_tasks_index'),
            activeOn: ['jabvox_tasks_index', 'jabvox_tasks_board'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_TICKETS
    )
      ? [
          {
            name: 'Jabvox Tickets',
            label: t('SIDEBAR.JABVOX_TICKETS'),
            icon: 'i-lucide-ticket',
            to: accountScopedRoute('jabvox_tickets_index'),
            activeOn: [
              'jabvox_tickets_index',
              'jabvox_tickets_detail',
              'jabvox_tickets_metrics',
              'jabvox_tickets_categories',
            ],
            component: leafProps =>
              h(TicketsSidebarLeaf, {
                label: leafProps.label,
                icon: leafProps.icon,
              }),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_INTERNAL_CHAT
    )
      ? [
          {
            name: 'Jabvox Internal Chat',
            label: t('JABVOX_INTERNAL_CHAT.SIDEBAR_LABEL'),
            icon: 'i-lucide-message-circle',
            to: accountScopedRoute('jabvox_internal_chat_index'),
            activeOn: ['jabvox_internal_chat_index'],
            getterKeys: { count: 'jabvoxInternalChat/getUnreadTotal' },
          },
        ]
      : []),
    ...(canViewJabvoxReports.value
      ? [
          {
            name: 'Jabvox Reports',
            label: t('JABVOX_REPORTS.SIDEBAR_LABEL'),
            icon: 'i-lucide-bar-chart-2',
            to: accountScopedRoute('jabvox_reports_index'),
            activeOn: ['jabvox_reports_index'],
          },
        ]
      : []),
    ...(canViewJabvoxReports.value &&
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.JABVOX_CDR)
      ? [
          {
            name: 'Jabvox CDR Reports',
            label: t('JABVOX_CDR.SIDEBAR_LABEL'),
            icon: 'i-lucide-phone-call',
            to: accountScopedRoute('jabvox_cdr_reports_index'),
            activeOn: ['jabvox_cdr_reports_index'],
          },
        ]
      : []),
    ...(canViewJabvoxReports.value &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_AUDIO_ANALYSIS
    )
      ? [
          {
            name: 'Jabvox Audio Reports',
            label: t('JABVOX_AUDIO.SIDEBAR_LABEL'),
            icon: 'i-lucide-audio-lines',
            to: accountScopedRoute('jabvox_audio_analysis_reports_index'),
            activeOn: ['jabvox_audio_analysis_reports_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_ANALYTICS
    )
      ? [
          {
            name: 'Jabvox Analytics',
            label: t('SIDEBAR.JABVOX_ANALYTICS'),
            icon: 'i-lucide-chart-no-axes-combined',
            to: accountScopedRoute('jabvox_analytics_index'),
            activeOn: [
              'jabvox_analytics_index',
              'jabvox_analytics_data_sources',
              'jabvox_analytics_dashboards',
              'jabvox_analytics_permissions',
            ],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_CUSTOMIZATION
    ) && checkPermissions(['administrator', 'jabvox_custom_data_manage'])
      ? [
          {
            name: 'Jabvox Custom Data',
            label: t('SIDEBAR.JABVOX_CUSTOM_DATA'),
            icon: 'i-lucide-database',
            to: accountScopedRoute('jabvox_custom_data_index'),
            activeOn: ['jabvox_custom_data_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_CLINICA
    ) && checkPermissions(['administrator', 'agent'])
      ? [
          {
            name: 'Jabvox Clinica',
            label: t('JABVOX_CLINICA.SIDEBAR_LABEL'),
            icon: 'i-lucide-stethoscope',
            to: accountScopedRoute('jabvox_clinica_index'),
            activeOn: ['jabvox_clinica_index'],
          },
        ]
      : []),
    ...(isCallsAvailable.value
      ? [
          {
            name: 'Calls',
            label: t('SIDEBAR.CALLS'),
            icon: 'i-lucide-phone',
            to: accountScopedRoute('calls_dashboard_index'),
            activeOn: ['calls_dashboard_index'],
          },
        ]
      : []),
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index', 'companies_dashboard_show'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
        {
          name: 'Reports WhatsApp',
          label: t('SIDEBAR.REPORTS_WHATSAPP'),
          to: accountScopedRoute('whatsapp_reports'),
        },
        {
          name: 'Reports WhatsApp Templates',
          label: t('SIDEBAR.REPORTS_WHATSAPP_TEMPLATES'),
          to: accountScopedRoute('whatsapp_templates_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.JABVOX_DIALER
    )
      ? [
          {
            name: 'Jabvox Dialer',
            label: t('JABVOX_DIALER.TITLE'),
            icon: 'i-lucide-phone-call',
            to: accountScopedRoute('jabvox_dialer_index'),
            activeOn: ['jabvox_dialer_index'],
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.HELP_CENTER
    )
      ? [
          {
            name: 'Portals',
            label: t('SIDEBAR.HELP_CENTER.TITLE'),
            icon: 'i-lucide-library-big',
            children: [
              {
                name: 'Articles',
                label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
                activeOn: [
                  'portals_articles_index',
                  'portals_articles_new',
                  'portals_articles_edit',
                ],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_articles_index',
                }),
              },
              {
                name: 'Categories',
                label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
                activeOn: [
                  'portals_categories_index',
                  'portals_categories_articles_index',
                  'portals_categories_articles_edit',
                ],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_categories_index',
                }),
              },
              {
                name: 'Locales',
                label: t('SIDEBAR.HELP_CENTER.LOCALES'),
                activeOn: ['portals_locales_index'],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_locales_index',
                }),
              },
              {
                name: 'Settings',
                label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
                activeOn: ['portals_settings_index'],
                to: accountScopedRoute('portals_index', {
                  navigationPath: 'portals_settings_index',
                }),
              },
            ],
          },
        ]
      : []),
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        ...(isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_ADVANCED_AUTOMATIONS
        )
          ? [
              {
                name: 'Settings Jabvox Advanced Automations',
                label: t('SIDEBAR.ADVANCED_AUTOMATION'),
                icon: 'i-lucide-workflow',
                to: accountScopedRoute('jabvox_advanced_automations_settings'),
                activeOn: ['jabvox_advanced_automations_settings'],
              },
            ]
          : []),
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        ...(hasDataImport.value
          ? [
              {
                name: 'Settings Data',
                label: t('SIDEBAR.DATA'),
                icon: 'i-lucide-database',
                to: accountScopedRoute('settings_data_imports'),
              },
            ]
          : []),
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        // Only when the account has the custom-roles module enabled (the route
        // guard also blocks direct-URL access when it's off).
        ...(isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.CUSTOM_ROLES
        )
          ? [
              {
                name: 'Settings Custom Roles',
                label: t('SIDEBAR.CUSTOM_ROLES'),
                icon: 'i-lucide-shield-plus',
                to: accountScopedRoute('custom_roles_list'),
              },
            ]
          : []),
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
        ...(jabvoxCan('jabvox_field_visibilities_manage')
          ? [
              {
                name: 'Settings Jabvox Field Security',
                label: t('JABVOX_FIELD_SECURITY.SIDEBAR_LABEL'),
                icon: 'i-lucide-shield-check',
                to: accountScopedRoute('jabvox_field_security_index'),
                activeOn: ['jabvox_field_security_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_kanban_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_KANBAN
        )
          ? [
              {
                name: 'Settings Jabvox Kanban',
                label: t('SIDEBAR.JABVOX_KANBAN'),
                icon: 'i-lucide-kanban',
                activeOn: [
                  'jabvox_kanban_funnels_index',
                  'jabvox_kanban_funnels_new',
                  'jabvox_kanban_funnels_edit',
                  'jabvox_kanban_stages',
                ],
                to: accountScopedRoute('jabvox_kanban_funnels_index'),
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_products_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_PRODUCTS
        )
          ? [
              {
                name: 'Settings Jabvox Products',
                label: t('SIDEBAR.JABVOX_PRODUCTS'),
                icon: 'i-lucide-shopping-cart',
                to: accountScopedRoute('jabvox_products_index'),
                activeOn: ['jabvox_products_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_voip_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_VOIP
        )
          ? [
              {
                name: 'Settings Jabvox VoIP',
                label: t('SIDEBAR.JABVOX_VOIP'),
                icon: 'i-lucide-phone',
                to: accountScopedRoute('jabvox_voip_index'),
                activeOn: ['jabvox_voip_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_cdr_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_CDR
        )
          ? [
              {
                name: 'Settings Jabvox CDR',
                label: t('SIDEBAR.JABVOX_CDR'),
                icon: 'i-lucide-phone-call',
                to: accountScopedRoute('jabvox_cdr_settings_index'),
                activeOn: ['jabvox_cdr_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_whatsapp_templates_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_WHATSAPP_TEMPLATES
        )
          ? [
              {
                name: 'Settings Jabvox WA Templates',
                label: t('SIDEBAR.JABVOX_WHATSAPP_TEMPLATES'),
                icon: 'i-lucide-message-square-text',
                to: accountScopedRoute(
                  'jabvox_whatsapp_templates_settings_index'
                ),
                activeOn: ['jabvox_whatsapp_templates_settings_index'],
              },
            ]
          : []),
        // WhatsApp Flows (formularios interactivos) es un módulo distinto de
        // "Plantillas WA". A diferencia de templates, el backend exige
        // jabvox_whatsapp_flows_manage en TODAS las acciones del controller,
        // incluido el listado (BaseController#ensure_can_manage no distingue
        // lectura de escritura) — así que se gatea igual aquí, o un agente
        // sin el permiso vería la entrada y un 403 al abrirla.
        ...(jabvoxCan('jabvox_whatsapp_flows_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_WHATSAPP_FLOWS
        )
          ? [
              {
                name: 'Settings Jabvox WA Flows',
                label: t('SIDEBAR.JABVOX_WHATSAPP_FLOWS'),
                icon: 'i-lucide-list-tree',
                to: accountScopedRoute('jabvox_whatsapp_flows_settings_index'),
                activeOn: [
                  'jabvox_whatsapp_flows_settings_index',
                  'jabvox_whatsapp_flows_builder',
                ],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_bulk_whatsapp_send') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_BULK_WHATSAPP
        )
          ? [
              {
                name: 'Settings Jabvox Bulk WhatsApp',
                label: t('SIDEBAR.JABVOX_BULK_WHATSAPP'),
                icon: 'i-lucide-send-horizontal',
                to: accountScopedRoute('jabvox_bulk_whatsapp_settings_index'),
                activeOn: ['jabvox_bulk_whatsapp_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_whatsapp_groups_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_WHATSAPP_GROUPS
        )
          ? [
              {
                name: 'Settings Jabvox WhatsApp Groups',
                label: t('SIDEBAR.JABVOX_WHATSAPP_GROUPS'),
                icon: 'i-lucide-users-round',
                to: accountScopedRoute('jabvox_whatsapp_groups_settings_index'),
                activeOn: ['jabvox_whatsapp_groups_settings_index'],
              },
            ]
          : []),
        ...((jabvoxCan('jabvox_voice_broadcast_manage') ||
          jabvoxCan('jabvox_dialer_manage')) &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_VOICE_BROADCAST
        )
          ? [
              {
                name: 'Settings Jabvox Voice Broadcast',
                label: t('SIDEBAR.JABVOX_VOICE_BROADCAST'),
                icon: 'i-lucide-megaphone',
                to: accountScopedRoute('jabvox_voice_broadcast_settings_index'),
                activeOn: ['jabvox_voice_broadcast_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_audio_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_AUDIO_ANALYSIS
        )
          ? [
              {
                name: 'Settings Jabvox Audio Analysis',
                label: t('SIDEBAR.JABVOX_AUDIO_ANALYSIS'),
                icon: 'i-lucide-audio-lines',
                to: accountScopedRoute('jabvox_audio_analysis_settings_index'),
                activeOn: ['jabvox_audio_analysis_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_lead_import_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_LEAD_IMPORT
        )
          ? [
              {
                name: 'Settings Jabvox Lead Import',
                label: t('SIDEBAR.JABVOX_LEAD_IMPORT'),
                icon: 'i-lucide-database',
                to: accountScopedRoute('jabvox_lead_import_settings_index'),
                activeOn: ['jabvox_lead_import_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_data_export_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_DATA_EXPORT
        )
          ? [
              {
                name: 'Settings Jabvox Data Export',
                label: t('SIDEBAR.JABVOX_DATA_EXPORT'),
                icon: 'i-lucide-database-zap',
                to: accountScopedRoute('jabvox_data_export_settings_index'),
                activeOn: ['jabvox_data_export_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_typifications_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_CLICK_TO_CALL
        )
          ? [
              {
                name: 'Settings Jabvox Typifications',
                label: t('SIDEBAR.JABVOX_TYPIFICATIONS'),
                icon: 'i-lucide-tags',
                to: accountScopedRoute('jabvox_typifications_index'),
                activeOn: ['jabvox_typifications_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_saldo_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_SALDO
        )
          ? [
              {
                name: 'Settings Jabvox Saldo',
                label: t('SIDEBAR.JABVOX_SALDO'),
                icon: 'i-lucide-wallet',
                to: accountScopedRoute('jabvox_saldo_index'),
                activeOn: ['jabvox_saldo_index'],
              },
            ]
          : []),
        ...(isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_AI_CHAT
        )
          ? [
              {
                name: 'Settings Jabvox AI Budget',
                label: t('SIDEBAR.JABVOX_AI_BUDGET'),
                icon: 'i-lucide-coins',
                to: accountScopedRoute('jabvox_ai_budget_index'),
                activeOn: ['jabvox_ai_budget_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_sms_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_SMS
        )
          ? [
              {
                name: 'Settings Jabvox SMS',
                label: t('SIDEBAR.JABVOX_SMS'),
                icon: 'i-lucide-message-square',
                to: accountScopedRoute('jabvox_sms_index'),
                activeOn: ['jabvox_sms_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_ip_whitelist_manage')
          ? [
              {
                name: 'Settings Jabvox IP Whitelist',
                label: t('SIDEBAR.JABVOX_IP_WHITELIST'),
                icon: 'i-lucide-shield',
                to: accountScopedRoute('jabvox_ip_whitelist_index'),
                activeOn: ['jabvox_ip_whitelist_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_ai_chat_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_AI_CHAT
        )
          ? [
              {
                name: 'Settings Jabvox AI Chat',
                label: t('JABVOX_AI_CHAT.CONFIG_SIDEBAR_LABEL'),
                icon: 'i-ri-robot-2-line',
                to: accountScopedRoute('jabvox_ai_chat_settings_index'),
                activeOn: ['jabvox_ai_chat_settings_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_management_states_manage')
          ? [
              {
                name: 'Settings Jabvox Management States',
                label: t('JABVOX_MANAGEMENT_STATES.SIDEBAR_LABEL'),
                icon: 'i-lucide-tag',
                to: accountScopedRoute('jabvox_management_states_index'),
                activeOn: ['jabvox_management_states_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_dialer_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_DIALER
        )
          ? [
              {
                name: 'Settings Jabvox Dialer',
                label: t('JABVOX_DIALER.SETTINGS.TITLE'),
                icon: 'i-lucide-phone-call',
                to: accountScopedRoute('jabvox_dialer_settings'),
                activeOn: ['jabvox_dialer_settings'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_affiliates_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_AFFILIATES
        )
          ? [
              {
                name: 'Settings Jabvox Affiliates',
                label: t('JABVOX_AFFILIATES.TITLE'),
                icon: 'i-lucide-users-round',
                to: accountScopedRoute('jabvox_affiliates_index'),
                activeOn: ['jabvox_affiliates_index'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_email_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_EMAIL
        )
          ? [
              {
                name: 'Settings Jabvox Email',
                label: t('JABVOX_EMAIL.SIDEBAR_LABEL'),
                icon: 'i-lucide-mail',
                to: accountScopedRoute('jabvox_email_settings'),
                activeOn: ['jabvox_email_settings'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_forms_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_FORMS
        )
          ? [
              {
                name: 'Settings Jabvox Forms',
                label: t('JABVOX_FORMS.SIDEBAR_LABEL'),
                icon: 'i-lucide-file-text',
                to: accountScopedRoute('jabvox_forms_settings'),
                activeOn: ['jabvox_forms_settings'],
              },
            ]
          : []),
        ...(jabvoxCan('jabvox_custom_data_manage') &&
        isFeatureEnabledonAccount.value(
          accountId.value,
          FEATURE_FLAGS.JABVOX_CUSTOMIZATION
        )
          ? [
              {
                name: 'Settings Jabvox Customization',
                label: t('JABVOX_CUSTOMIZATION.SIDEBAR_LABEL'),
                icon: 'i-lucide-sliders-horizontal',
                to: accountScopedRoute('jabvox_customization_settings'),
                activeOn: ['jabvox_customization_settings'],
              },
            ]
          : []),
      ],
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-gradient-to-b from-[#DAEBDB] to-[#A8D5D3] flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-[#8FC4C2]"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="flex-shrink-0 flex items-center">
            <img
              :src="JABVOX_LOGO_URL"
              alt="Jabvox"
              class="h-5 w-auto object-contain"
            />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div v-if="!isEffectivelyCollapsed && isSaldoEnabled" class="px-2">
        <div
          class="px-2 py-1 rounded-lg bg-n-alpha-2 flex items-center justify-between text-xs"
        >
          <span class="text-n-slate-11">{{
            t('JABVOX_SALDO.HEADER.LABEL')
          }}</span>
          <span class="font-medium" :class="saldoBalanceColor">{{
            saldoLabel
          }}</span>
        </div>
      </div>
      <div
        v-if="!isEffectivelyCollapsed"
        v-on-click-outside="() => (presenceDropdownOpen = false)"
        class="px-2 relative"
      >
        <button
          class="w-full px-2 py-1 rounded-lg bg-n-alpha-2 flex items-center gap-2 text-xs hover:bg-n-alpha-3 transition-colors"
          @click="presenceDropdownOpen = !presenceDropdownOpen"
        >
          <span
            class="w-2 h-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: presenceColor }"
          />
          <span class="flex-1 text-left text-n-slate-11">{{
            presenceName
          }}</span>
          <span
            class="i-lucide-chevrons-up-down w-3 h-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <div
          v-if="presenceDropdownOpen"
          class="absolute top-full left-0 right-0 mt-1 bg-n-surface-2 border border-n-weak rounded-xl shadow-lg overflow-hidden z-50"
        >
          <button
            class="w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-n-alpha-2"
            :class="
              !currentAppState
                ? 'font-semibold text-n-slate-12'
                : 'text-n-slate-11'
            "
            @click="setPresence(null)"
          >
            <span class="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
            {{
              currentAppState
                ? t('JABVOX_MANAGEMENT_STATES.APP_STATES.ACTIVE_RETURN')
                : t('JABVOX_MANAGEMENT_STATES.APP_STATES.ACTIVE_DEFAULT')
            }}
          </button>
          <button
            v-for="state in appStatesForPresence"
            :key="state.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-n-alpha-2"
            :class="
              currentAppState?.id === state.id
                ? 'font-semibold text-n-slate-12'
                : 'text-n-slate-11'
            "
            @click="setPresence(state.id)"
          >
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: state.color }"
            />
            {{ state.name }}
          </button>
        </div>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-[#A8D5D3] to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnJabvoxCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnJabvoxCloud && !isACustomBrandedInstance && isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-[#8FC4C2]/60 shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
